import { Component, OnInit, inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { TelemetryService } from '../../services/telemetry.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Breadcrumb } from '../../models/session.model';
import { FormsModule } from '@angular/forms';

import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { delay } from 'rxjs/operators';

@Component({
  selector: 'app-log-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, ThinkingOrbComponent],
  template: `
    <div class="p-8 max-w-5xl mx-auto min-h-screen">
      <!-- Header -->
      <div class="flex justify-between items-center mb-8">
        <button (click)="goBack()" class="text-gray-400 hover:text-white flex items-center gap-2 transition-colors font-mono uppercase tracking-widest text-[10px] font-bold">
          <i class="ph ph-arrow-left text-lg"></i>
          Volver a Logs
        </button>
        <div class="flex gap-4 items-center">
          <div class="text-[10px] text-gray-500 font-mono uppercase tracking-widest" *ngIf="sessionId">
            Sesión: {{ sessionId }}
          </div>
          
          <select [(ngModel)]="selectedLogLevel" class="bg-black border border-zaxis-border px-3 py-1.5 text-[9px] font-mono tracking-widest uppercase text-white focus:outline-none focus:border-zaxis-accent cursor-pointer rounded-none">
            <option value="all">Todos los niveles</option>
            <option value="error">Errores / Crashes</option>
            <option value="warning">Advertencias</option>
            <option value="info">Info / Otros</option>
          </select>
        </div>
      </div>

      <div class="bg-black border border-zaxis-border p-6 rounded-none relative overflow-hidden mb-6">
        <div class="absolute top-0 left-0 w-2 h-full bg-zaxis-accent"></div>
        <div class="relative z-10 flex justify-between items-center pl-4">
          <div>
            <h1 class="text-2xl font-mono font-black tracking-tight text-white flex items-center gap-3 uppercase">
              Visor de Logs
            </h1>
            <p class="text-gray-400 mt-2 text-[10px] font-mono tracking-widest uppercase">
              Eventos y excepciones registrados en texto puro.
            </p>
          </div>
        </div>
      </div>

      <!-- Custom Brutalist Skeleton Loading State -->
      <div *ngIf="loading" class="space-y-3">
        <div *ngFor="let mock of [1,2,3,4,5,6]" class="skeleton-box p-4 h-[60px] flex items-center gap-4">
          <div class="skeleton-text w-24" style="margin: 0"></div>
          <div class="skeleton-text w-20" style="margin: 0"></div>
          <div class="skeleton-text w-full" style="margin: 0"></div>
        </div>
      </div>

      <!-- Empty State -->
      <div *ngIf="!loading && breadcrumbs.length === 0" class="text-center py-20 bg-black rounded-none border border-zaxis-border">
        <div class="flex justify-center mb-4">
          <i class="ph ph-terminal text-6xl text-gray-600"></i>
        </div>
        <p class="text-gray-400 text-sm font-mono uppercase tracking-widest">No hay logs registrados en esta sesión.</p>
      </div>

      <!-- Logs List -->
      <div *ngIf="!loading && breadcrumbs.length > 0" class="space-y-3">
        <div *ngFor="let log of filteredBreadcrumbs" 
             class="bg-black border rounded-none overflow-hidden font-mono text-[11px] transition-all"
             [ngClass]="{
               'border-zaxis-danger': isCrash(log) || isError(log),
               'border-zaxis-warning': isWarning(log),
               'border-zaxis-border': !isCrash(log) && !isError(log) && !isWarning(log)
             }">
          
          <!-- Log Header -->
          <div class="p-4 flex items-start gap-4" [ngClass]="{
              'bg-zaxis-danger/10': isCrash(log) || isError(log), 
              'bg-zaxis-warning/10': isWarning(log)
            }">
            <div class="w-24 flex-shrink-0 text-gray-500 text-[10px] mt-0.5">
              {{ log.timestamp | date:'HH:mm:ss.SSS' }}
            </div>
            <div class="w-20 flex-shrink-0">
              <span class="px-2 py-0.5 rounded-none text-[9px] font-bold uppercase tracking-widest border"
                    [ngClass]="{
                      'bg-zaxis-danger/10 text-zaxis-danger border-zaxis-danger': isCrash(log) || isError(log),
                      'bg-zaxis-warning/10 text-zaxis-warning border-zaxis-warning': isWarning(log),
                      'bg-black text-gray-400 border-zaxis-border': !isCrash(log) && !isError(log) && !isWarning(log)
                    }">
                {{ log.category }}
              </span>
            </div>
            <div class="flex-1 text-gray-200">
              {{ log.message }}
            </div>
            <div *ngIf="log.exception" class="flex-shrink-0">
              <button (click)="toggleException(log)" class="text-zaxis-danger border border-zaxis-danger hover:bg-zaxis-danger/20 text-[9px] font-mono font-bold uppercase tracking-widest px-3 py-1 bg-zaxis-danger/10 rounded-none transition-colors">
                {{ isExpanded(log) ? 'Ocultar Trace' : 'Ver Trace' }}
              </button>
            </div>
          </div>

          <!-- Exception Details -->
          <div *ngIf="log.exception && isExpanded(log)" class="p-4 bg-black border-t border-zaxis-border text-[9px] text-gray-400 overflow-x-auto">
            <div class="mb-2 border-b border-zaxis-border pb-2">
              <span class="text-zaxis-danger font-bold uppercase tracking-widest">{{ log.exception.exceptionName }}</span>
              <span class="text-gray-300 ml-2">{{ log.exception.exceptionMessage }}</span>
            </div>
            <div class="whitespace-pre-wrap font-mono text-gray-500 mt-2">{{ log.exception.stackTrace }}</div>
          </div>

        </div>

        <!-- Load More -->
        <div class="pt-6 flex justify-center" *ngIf="hasMoreLogs">
          <button (click)="loadMore()" [disabled]="loadingMore" class="px-6 py-2 bg-black hover:bg-white/5 border border-zaxis-border rounded-none text-[9px] font-mono font-bold uppercase tracking-widest transition-colors disabled:opacity-50 flex items-center gap-2">
            <app-thinking-orb *ngIf="loadingMore" state="working" [size]="16" class="text-zaxis-accent"></app-thinking-orb>
            {{ loadingMore ? 'Cargando...' : 'Cargar más' }}
          </button>
        </div>
      </div>

    </div>
  `
})
export class LogDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private telemetryService = inject(TelemetryService);
  private platformId = inject(PLATFORM_ID);

  sessionId: string | null = null;
  loading = true;
  loadingMore = false;

  allFetchedLogs: Breadcrumb[] = [];
  breadcrumbs: Breadcrumb[] = [];
  
  currentChunkPage = 1;
  chunkLimit = 20; // Traer hasta 20 chunks del backend por petición para contrarrestar chunks pequeños
  displayLimit = 50; // Mostrar de 50 en 50 en la UI
  
  hasMoreBackendChunks = true;
  hasMoreLogs = true;
  
  selectedLogLevel = 'all';

  expandedLogs = new Set<number>();

  get filteredBreadcrumbs() {
    if (this.selectedLogLevel === 'all') return this.breadcrumbs;
    return this.breadcrumbs.filter(log => {
      if (this.selectedLogLevel === 'error') return this.isCrash(log) || this.isError(log);
      if (this.selectedLogLevel === 'warning') return log.category?.toLowerCase() === 'warning';
      if (this.selectedLogLevel === 'info') return !this.isCrash(log) && !this.isError(log) && log.category?.toLowerCase() !== 'warning';
      return true;
    });
  }

  ngOnInit() {
    this.sessionId = this.route.snapshot.paramMap.get('id');
    if (this.sessionId) {
      this.loadLogs(true);
    }
  }

  goBack() {
    this.router.navigate(['/logs']);
  }

  loadLogs(initial: boolean = false) {
    if (!this.sessionId) return;
    
    if (initial) {
      this.loading = true;
      this.allFetchedLogs = [];
      this.breadcrumbs = [];
      this.currentChunkPage = 1;
      this.hasMoreBackendChunks = true;
      this.hasMoreLogs = true;
    } else {
      this.loadingMore = true;
    }

    const currentDisplayed = this.breadcrumbs.length;
    const availableInMemory = this.allFetchedLogs.length;

    // Si ya tenemos suficientes logs en memoria, o si el backend no tiene más chunks,
    // simplemente mostramos los que ya tenemos cacheados.
    if (availableInMemory >= currentDisplayed + this.displayLimit || !this.hasMoreBackendChunks) {
      this.showMoreFromMemory();
      return;
    }

    // Necesitamos traer el siguiente chunk del backend
    const stream = this.telemetryService.getSessionBreadcrumbs(this.sessionId, this.currentChunkPage, this.chunkLimit);
    const delayedStream = isPlatformBrowser(this.platformId) ? stream.pipe(delay(500)) : stream;
    
    delayedStream.subscribe({
      next: (response: any) => {
        let newLogs: Breadcrumb[] = [];
        if (Array.isArray(response)) {
          response.forEach((chunk: any) => {
             if (chunk.items && Array.isArray(chunk.items)) {
               newLogs = [...newLogs, ...chunk.items];
             }
          });
        }
        
        // Si nos devolvieron menos chunks de los solicitados, ya no hay más en el backend
        if (response.length < this.chunkLimit) {
          this.hasMoreBackendChunks = false;
        }

        this.allFetchedLogs = [...this.allFetchedLogs, ...newLogs];
        this.allFetchedLogs.sort((a, b) => a.timestamp - b.timestamp);

        this.currentChunkPage++;

        this.showMoreFromMemory();
      },
      error: (err) => {
        console.error('Error loading logs', err);
        this.loading = false;
        this.loadingMore = false;
      }
    });
  }

  showMoreFromMemory() {
    const nextLength = this.breadcrumbs.length + this.displayLimit;
    this.breadcrumbs = this.allFetchedLogs.slice(0, nextLength);
    
    // Si ya mostramos todos los logs en memoria y no hay más en el backend, ocultamos el botón
    if (this.breadcrumbs.length >= this.allFetchedLogs.length && !this.hasMoreBackendChunks) {
      this.hasMoreLogs = false;
    } else {
      this.hasMoreLogs = true;
    }

    this.loading = false;
    this.loadingMore = false;
  }

  loadMore() {
    this.loadLogs();
  }

  isCrash(log: Breadcrumb): boolean {
    return log.category?.toLowerCase() === 'crash';
  }

  isError(log: Breadcrumb): boolean {
    return log.category?.toLowerCase() === 'error' || log.category?.toLowerCase() === 'exception';
  }

  isWarning(log: Breadcrumb): boolean {
    return log.category?.toLowerCase() === 'warning';
  }

  toggleException(log: Breadcrumb) {
    if (this.expandedLogs.has(log.timestamp)) {
      this.expandedLogs.delete(log.timestamp);
    } else {
      this.expandedLogs.add(log.timestamp);
    }
  }

  isExpanded(log: Breadcrumb): boolean {
    return this.expandedLogs.has(log.timestamp);
  }
}
